import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import {
  ArrowLeft,
  CalendarDays,
  ChefHat,
  Check,
  X,
  Send,
  MessageSquare,
  Utensils,
  PackageCheck,
  Info,
} from "lucide-react";
import { ErrorToast, SuccessToast } from "../../Helper/FormHelper";
import {
  fetchWishDetails,
  fetchWishNegotiations,
  orderFromWish,
  sendBuyerNegotiation,
  updateWishOrderStatus,
} from "./wish-order-pricing.api";
import AddressPicker from "../checkout/AddressPicker";
import {
  WISH_STATUS,
  formatDateTime,
  isPendingStatus,
  mapNegotiationsToMessages,
  mapWishToView,
} from "./wish-order-pricing.utils";
import "./wish-ordar-pricing.css";

/* ── Status config ───────────────────────────── */
const STATUS_CFG = {
  pending: {
    label: "Pending",
    bg: "#fef3c7",
    color: "#92400e",
    dot: "#f59e0b",
  },
  accepted: {
    label: "Accepted",
    bg: "#dcfce7",
    color: "#166534",
    dot: "#059669",
  },
  rejected: {
    label: "Rejected",
    bg: "#fee2e2",
    color: "#991b1b",
    dot: "#ef4444",
  },
  cancelled: {
    label: "Cancelled",
    bg: "#fee2e2",
    color: "#991b1b",
    dot: "#ef4444",
  },
  completed: {
    label: "Completed",
    bg: "#dbeafe",
    color: "#1e40af",
    dot: "#0d6efd",
  },
};

function StatusChip({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        background: cfg.bg,
        color: cfg.color,
        borderRadius: 20,
        padding: "4px 12px",
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "'Inter', sans-serif",
        textTransform: "capitalize",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: cfg.dot,
        }}
      />
      {cfg.label}
    </span>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div style={{ marginTop: 1, flexShrink: 0, color: "#9ca3af" }}>
        {icon}
      </div>
      <div>
        <p
          style={{
            margin: 0,
            fontSize: 11,
            color: "#9ca3af",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </p>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: "#374151",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 500,
            lineHeight: 1.5,
          }}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

export default function WishOrderPricing() {
  const [searchParams] = useSearchParams();
  const wishId = searchParams.get("wishId");
  const chatEndRef = useRef(null);

  const {
    data: wishDetails,
    isPending,
    error,
    refetch: refetchWish,
  } = useQuery({
    queryKey: ["buyerWishById", wishId],
    queryFn: () => fetchWishDetails(wishId),
    enabled: !!wishId,
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const {
    data: negotiations = [],
    isPending: isNegPending,
    refetch: refetchNeg,
  } = useQuery({
    queryKey: ["buyerWishNegotiations", wishId],
    queryFn: () => fetchWishNegotiations(wishId),
    enabled: !!wishId,
    staleTime: 10000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval:
      wishDetails?.status === WISH_STATUS.PENDING ? 15000 : false,
  });

  const [counterOffer, setCounterOffer] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [imgError, setImgError] = useState(false);
  // Checkout of an accepted wish.
  const [orderAddressId, setOrderAddressId] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const wish = useMemo(() => mapWishToView(wishDetails), [wishDetails]);
  const messages = useMemo(
    () => mapNegotiationsToMessages(negotiations),
    [negotiations],
  );
  const status = wish?.status || WISH_STATUS.PENDING;
  const isDisabled = !isPendingStatus(status);
  const sellerStarted = messages.some((m) => m?.actor === "SELLER");
  const sellerLink = wish?.sellerId
    ? `/SellerProfile/${wish.sellerId}`
    : "/all-kitchens";
  const hasValidOffer = Number(counterOffer) > 0;
  const statusCfg = STATUS_CFG[status] || STATUS_CFG.pending;

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleCounterOffer = useCallback(
    async (e) => {
      e.preventDefault();
      if (isSubmitting || isDisabled) return;
      const amount = Number(counterOffer);
      if (!amount || amount <= 0) {
        ErrorToast("Please enter a valid price.");
        return;
      }
      try {
        setIsSubmitting(true);
        await sendBuyerNegotiation(wishId, amount);
        setCounterOffer("");
        await refetchNeg();
        SuccessToast("Offer sent!");
      } catch (err) {
        ErrorToast(err?.message || "Failed to send offer.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [counterOffer, isDisabled, isSubmitting, refetchNeg, wishId],
  );

  const handleStatus = useCallback(
    async (newStatus) => {
      if (!wishId || isUpdating || isSubmitting || isDisabled) return;
      try {
        setIsUpdating(true);
        await updateWishOrderStatus(wishId, newStatus);
        await Promise.all([refetchWish(), refetchNeg()]);
        SuccessToast(`Order ${newStatus} successfully.`);
      } catch (err) {
        ErrorToast(err?.message || `Failed to ${newStatus} order.`);
      } finally {
        setIsUpdating(false);
      }
    },
    [isDisabled, isSubmitting, isUpdating, refetchWish, refetchNeg, wishId],
  );

  // Safe to retry: a wish that already produced an order returns that order.
  const handlePlaceWishOrder = useCallback(async () => {
    if (!wishId || !orderAddressId || isPlacingOrder) return;
    try {
      setIsPlacingOrder(true);
      const order = await orderFromWish(wishId, { addressId: orderAddressId });
      SuccessToast("Order placed!");
      window.location.href = `/order-details/${order._id}`;
    } catch (err) {
      ErrorToast(err?.message || "Could not place the order.");
    } finally {
      setIsPlacingOrder(false);
    }
  }, [wishId, orderAddressId, isPlacingOrder]);

  if (!wishId)
    return (
      <div className="home-content-shell page-navbar-space pb-5">
        <div
          style={{
            padding: "16px",
            borderRadius: 10,
            background: "#fef3c7",
            color: "#92400e",
            fontSize: 13,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Wish order ID is missing. Please open this page from My Wishes.
        </div>
      </div>
    );

  if (!isPending && !wish)
    return (
      <div className="home-content-shell page-navbar-space pb-5">
        <div
          style={{
            padding: "16px",
            borderRadius: 10,
            background: "#fee2e2",
            color: "#991b1b",
            fontSize: 13,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Failed to load this wish order. Please try again.
        </div>
      </div>
    );

  const hasImg = wish?.wishImage && !imgError;

  console.log("Rendered wishDetails", JSON.stringify(wishDetails));

  return (
    <div className="home-content-shell page-navbar-space pb-5">
      {/* Back */}
      <Link
        to="/my-wishes"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 13,
          fontWeight: 600,
          color: "#6b7280",
          textDecoration: "none",
          fontFamily: "'Inter', sans-serif",
          marginBottom: 16,
        }}
      >
        <ArrowLeft size={15} /> My Wishes
      </Link>

      <div className="wop-layout">
        {/* ── LEFT: wish summary ── */}
        <div className="wop-left">
          {/* Image */}
          <div
            style={{
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: 14,
              position: "relative",
              background: "#f1f5f9",
            }}
          >
            {isPending ? (
              <Skeleton height={220} />
            ) : hasImg ? (
              <img
                src={wish.wishImage}
                alt={wish.wishName}
                onError={() => setImgError(true)}
                style={{
                  width: "100%",
                  height: 220,
                  objectFit: "cover",
                  display: "block",
                }}
              />
            ) : (
              <div
                style={{
                  height: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 8,
                  color: "#9ca3af",
                }}
              >
                <Utensils size={36} color="#d1d5db" />
                <span
                  style={{ fontSize: 12, fontFamily: "'Inter', sans-serif" }}
                >
                  No photo
                </span>
              </div>
            )}
            {!isPending && (
              <div style={{ position: "absolute", top: 10, right: 10 }}>
                <StatusChip status={status} />
              </div>
            )}
          </div>

          {/* Wish details card */}
          <div className="wop-card">
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 14,
              }}
            >
              {isPending ? (
                <Skeleton height={22} width="70%" />
              ) : (
                <h5
                  style={{
                    fontWeight: 800,
                    fontSize: 18,
                    margin: 0,
                    color: "#0f172a",
                    fontFamily: "'Inter', sans-serif",
                    lineHeight: 1.3,
                  }}
                >
                  {wish?.wishName}
                </h5>
              )}
              {!isPending && isPendingStatus(status) && (
                <button
                  onClick={() => handleStatus(WISH_STATUS.CANCELLED)}
                  disabled={isUpdating}
                  style={{
                    flexShrink: 0,
                    padding: "5px 12px",
                    borderRadius: 8,
                    border: "1px solid #fecaca",
                    background: "#fff5f5",
                    color: "#ef4444",
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: "'Inter', sans-serif",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {isPending ? (
                <Skeleton count={4} height={14} />
              ) : (
                <>
                  <InfoRow
                    icon={<PackageCheck size={14} />}
                    label="Quantity"
                    value={wish?.quantityText}
                  />
                  <InfoRow
                    icon={<CalendarDays size={14} />}
                    label="Desired Delivery"
                    value={wish?.wishDate}
                  />
                  {wish?.ingredientsText !== "No suggested ingredients" && (
                    <InfoRow
                      icon={<Utensils size={14} />}
                      label="Ingredients"
                      value={wish?.ingredientsText}
                    />
                  )}
                  {wish?.additionalInfoText !== "No additional info" && (
                    <InfoRow
                      icon={<Info size={14} />}
                      label="Additional Info"
                      value={wish?.additionalInfoText}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Seller info */}
          {!isPending && wish?.sellerName && (
            <div className="wop-card" style={{ marginTop: 12 }}>
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  margin: "0 0 10px",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Kitchen
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img
                  src={wish.sellerAvatar}
                  alt={wish.sellerName}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #f1f5f9",
                    flexShrink: 0,
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#111827",
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {wish.sellerName}
                  </p>
                  <Link
                    to={sellerLink}
                    style={{
                      fontSize: 12,
                      color: "#0d6efd",
                      textDecoration: "none",
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      marginTop: 2,
                    }}
                  >
                    <ChefHat size={11} /> View Kitchen
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── RIGHT: negotiation chat ── */}
        <div className="wop-right">
          <div className="wop-card wop-chat-wrap">
            {/* Chat header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
                paddingBottom: 12,
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <div>
                <h6
                  style={{
                    fontWeight: 800,
                    fontSize: 15,
                    margin: "0 0 2px",
                    fontFamily: "'Inter', sans-serif",
                    color: "#1a1a2e",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <MessageSquare size={15} color="#0d6efd" /> Price Negotiation
                </h6>
                <p
                  style={{
                    margin: 0,
                    fontSize: 11,
                    color: "#9ca3af",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {isDisabled
                    ? `This wish is ${status}`
                    : "Negotiate price with the seller"}
                </p>
              </div>
              {!isDisabled && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#059669",
                    background: "#dcfce7",
                    borderRadius: 20,
                    padding: "2px 8px",
                    fontFamily: "'Inter', sans-serif",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: "#059669",
                      animation: "pulse 1.5s infinite",
                    }}
                  />
                  Live
                </span>
              )}
            </div>

            {/* Messages area */}
            <div className="wop-messages">
              {isNegPending && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 12 }}
                >
                  {[1, 2].map((i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        gap: 8,
                        justifyContent: i % 2 === 0 ? "flex-end" : "flex-start",
                      }}
                    >
                      {i % 2 !== 0 && (
                        <Skeleton circle width={36} height={36} />
                      )}
                      <Skeleton height={72} width={220} borderRadius={12} />
                      {i % 2 === 0 && (
                        <Skeleton circle width={36} height={36} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {!isNegPending && messages.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 20px",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <MessageSquare
                    size={32}
                    color="#e5e7eb"
                    style={{ marginBottom: 10 }}
                  />
                  <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>
                    No messages yet. The seller will respond soon.
                  </p>
                </div>
              )}

              {messages.map((msg, idx) => {
                const isSeller = msg.from === "seller";
                const isLastMsg = idx === messages.length - 1;
                return (
                  <motion.div
                    key={`${msg.id}-${idx}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05, duration: 0.2 }}
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: isSeller ? "flex-start" : "flex-end",
                      marginBottom: 12,
                    }}
                  >
                    {/* Seller avatar */}
                    {isSeller && (
                      <img
                        src={
                          wish?.sellerAvatar ||
                          "/Assets/Img/kitchen_default.png"
                        }
                        alt="Seller"
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          objectFit: "cover",
                          flexShrink: 0,
                          border: "1.5px solid #f1f5f9",
                          marginTop: 2,
                        }}
                      />
                    )}

                    <div style={{ maxWidth: "72%" }}>
                      {/* Bubble */}
                      <div
                        style={{
                          padding: "10px 14px",
                          borderRadius: isSeller
                            ? "4px 14px 14px 14px"
                            : "14px 4px 14px 14px",
                          background: isSeller ? "#f8fafc" : "#0d6efd",
                          border: isSeller ? "1px solid #e5e7eb" : "none",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 4px",
                            fontSize: 11,
                            fontWeight: 600,
                            color: isSeller
                              ? "#9ca3af"
                              : "rgba(255,255,255,0.7)",
                            fontFamily: "'Inter', sans-serif",
                          }}
                        >
                          {isSeller ? wish?.sellerName || "Seller" : "You"}
                        </p>
                        <p
                          style={{
                            margin: "0 0 6px",
                            fontSize: 13,
                            color: isSeller ? "#374151" : "#fff",
                            fontFamily: "'Inter', sans-serif",
                          }}
                        >
                          {isSeller ? "I can prepare this for" : "My offer is"}
                        </p>
                        {/* Price callout */}
                        <div
                          style={{
                            display: "inline-block",
                            background: isSeller
                              ? "#0d6efd"
                              : "rgba(255,255,255,0.2)",
                            borderRadius: 8,
                            padding: "4px 12px",
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 18,
                              fontWeight: 900,
                              color: "#fff",
                              fontFamily: "'Inter', sans-serif",
                            }}
                          >
                            ৳{msg?.price?.toLocaleString("en-BD")}
                          </span>
                        </div>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 10,
                            color: isSeller
                              ? "#9ca3af"
                              : "rgba(255,255,255,0.6)",
                            fontFamily: "'Inter', sans-serif",
                          }}
                        >
                          {formatDateTime(msg?.createdAt)}
                        </p>
                      </div>

                      {/* Accept / Reject — only on last seller message when pending */}
                      {isSeller && isLastMsg && isPendingStatus(status) && (
                        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                          <button
                            onClick={() => handleStatus(WISH_STATUS.ACCEPTED)}
                            disabled={isUpdating || isSubmitting}
                            style={{
                              flex: 1,
                              padding: "7px 0",
                              borderRadius: 8,
                              border: "none",
                              background: "#059669",
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 700,
                              fontFamily: "'Inter', sans-serif",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                            }}
                          >
                            <Check size={13} /> {isUpdating ? "…" : "Accept"}
                          </button>
                          <button
                            onClick={() => handleStatus(WISH_STATUS.REJECTED)}
                            disabled={isUpdating || isSubmitting}
                            style={{
                              flex: 1,
                              padding: "7px 0",
                              borderRadius: 8,
                              border: "none",
                              background: "#f1f5f9",
                              color: "#374151",
                              fontSize: 12,
                              fontWeight: 700,
                              fontFamily: "'Inter', sans-serif",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                            }}
                          >
                            <X size={13} /> {isUpdating ? "…" : "Reject"}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Customer avatar */}
                    {!isSeller && (
                      <img
                        src="/Assets/Img/team/01.jpg"
                        alt="You"
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          objectFit: "cover",
                          flexShrink: 0,
                          border: "1.5px solid #f1f5f9",
                          marginTop: 2,
                        }}
                      />
                    )}
                  </motion.div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            {/* Finalized status banner */}
            {isDisabled && (
              <div
                style={{
                  margin: "0 0 0",
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: statusCfg.bg,
                  border: `1px solid ${statusCfg.dot}22`,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: statusCfg.dot,
                    flexShrink: 0,
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: statusCfg.color,
                    fontWeight: 600,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  This wish order has been{" "}
                  <strong style={{ textTransform: "capitalize" }}>
                    {status}
                  </strong>
                  .
                </p>
              </div>
            )}

            {/* An accepted wish is checked out like any other order — the API
                turns it into one at the agreed price. */}
            {status === WISH_STATUS.ACCEPTED && !wish?.orderId && (
              <div style={{ marginTop: 14 }}>
                <p
                  style={{
                    margin: "0 0 10px",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#374151",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Agreed price ৳{wish?.agreedPrice?.toLocaleString("en-BD")} — choose
                  where to deliver it.
                </p>
                <AddressPicker selectedId={orderAddressId} onSelect={setOrderAddressId} />
                <button
                  onClick={handlePlaceWishOrder}
                  disabled={!orderAddressId || isPlacingOrder}
                  style={{
                    width: "100%",
                    marginTop: 10,
                    padding: "11px",
                    borderRadius: 10,
                    border: "none",
                    background: !orderAddressId || isPlacingOrder ? "#d1d5db" : "#0d6efd",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 700,
                    fontFamily: "'Inter', sans-serif",
                    cursor: !orderAddressId || isPlacingOrder ? "not-allowed" : "pointer",
                  }}
                >
                  {isPlacingOrder ? "Placing order…" : "Place this order"}
                </button>
              </div>
            )}

            {wish?.orderId && (
              <Link
                to={`/order-details/${wish.orderId}`}
                style={{
                  display: "block",
                  marginTop: 14,
                  padding: "11px",
                  borderRadius: 10,
                  background: "#0d6efd",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  textAlign: "center",
                  textDecoration: "none",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                View your order
              </Link>
            )}

            {/* Counter offer form */}
            {!isDisabled && sellerStarted && (
              <form
                onSubmit={handleCounterOffer}
                style={{ paddingTop: 12, borderTop: "1px solid #f1f5f9" }}
              >
                <p
                  style={{
                    margin: "0 0 8px",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#374151",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Send Counter Offer
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ position: "relative", flex: 1 }}>
                    <span
                      style={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#374151",
                        fontFamily: "'Inter', sans-serif",
                        pointerEvents: "none",
                      }}
                    >
                      ৳
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="10000000"
                      placeholder="Your price"
                      value={counterOffer}
                      onChange={(e) => setCounterOffer(e.target.value)}
                      disabled={isSubmitting || isUpdating}
                      style={{
                        width: "100%",
                        padding: "10px 12px 10px 28px",
                        borderRadius: 8,
                        border: "1.5px solid #e5e7eb",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#111827",
                        fontFamily: "'Inter', sans-serif",
                        outline: "none",
                        boxSizing: "border-box",
                        background: "#f9fafb",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#0d6efd";
                        e.target.style.background = "#fff";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#e5e7eb";
                        e.target.style.background = "#f9fafb";
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting || isUpdating || !hasValidOffer}
                    style={{
                      padding: "10px 18px",
                      borderRadius: 8,
                      border: "none",
                      background: hasValidOffer ? "#0d6efd" : "#f1f5f9",
                      color: hasValidOffer ? "#fff" : "#9ca3af",
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: "'Inter', sans-serif",
                      cursor: hasValidOffer ? "pointer" : "not-allowed",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      flexShrink: 0,
                    }}
                  >
                    <Send size={14} /> {isSubmitting ? "Sending…" : "Send"}
                  </button>
                </div>
              </form>
            )}

            {/* Waiting for seller */}
            {!isDisabled && !sellerStarted && !isNegPending && (
              <div
                style={{
                  paddingTop: 12,
                  borderTop: "1px solid #f1f5f9",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    fontSize: 12,
                    color: "#9ca3af",
                    margin: 0,
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Waiting for the seller to make an offer. You can send your
                  counter offer once they respond.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
